import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package, Plus, Edit, Trash2, Check, Star, Zap, Crown,
  Calendar, DollarSign, Users, TrendingUp
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';

const PackagesManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showAddPackage, setShowAddPackage] = useState(false);
  const [newPackage, setNewPackage] = useState({
    name_ar: '',
    name_en: '',
    description_ar: '',
    description_en: '',
    price: '',
    duration_months: '',
    features: []
  });

  const defaultPackages = [
    {
      id: '1',
      name_ar: 'الباقة البرونزية',
      name_en: 'Bronze Package',
      description_ar: 'مثالية للقضايا البسيطة',
      description_en: 'Ideal for simple cases',
      price: 1500,
      duration_months: 3,
      icon: Package,
      color: 'from-amber-700 to-amber-800',
      features: [
        i18n.language === 'ar' ? 'استشارة قانونية مجانية' : 'Free legal consultation',
        i18n.language === 'ar' ? 'متابعة قضية واحدة' : 'One case follow-up',
        i18n.language === 'ar' ? 'الرد على الاستفسارات' : 'Respond to inquiries',
        i18n.language === 'ar' ? 'دعم فني محدود' : 'Limited technical support'
      ],
      subscribers: 12,
      badge: null
    },
    {
      id: '2',
      name_ar: 'الباقة الفضية',
      name_en: 'Silver Package',
      description_ar: 'للقضايا المتوسطة',
      description_en: 'For medium cases',
      price: 3500,
      duration_months: 6,
      icon: Star,
      color: 'from-gray-400 to-gray-600',
      features: [
        i18n.language === 'ar' ? 'كل ميزات البرونزية' : 'All Bronze features',
        i18n.language === 'ar' ? 'متابعة 3 قضايا' : 'Follow 3 cases',
        i18n.language === 'ar' ? 'صياغة مذكرات' : 'Draft memorandums',
        i18n.language === 'ar' ? 'دعم فني ممتاز' : 'Premium support',
        i18n.language === 'ar' ? 'مساعد AI' : 'AI Assistant'
      ],
      subscribers: 28,
      badge: i18n.language === 'ar' ? 'الأكثر شعبية' : 'Most Popular'
    },
    {
      id: '3',
      name_ar: 'الباقة الذهبية',
      name_en: 'Gold Package',
      description_ar: 'للقضايا المعقدة',
      description_en: 'For complex cases',
      price: 7500,
      duration_months: 12,
      icon: Crown,
      color: 'from-yellow-400 to-yellow-600',
      features: [
        i18n.language === 'ar' ? 'كل ميزات الفضية' : 'All Silver features',
        i18n.language === 'ar' ? 'متابعة غير محدودة' : 'Unlimited follow-up',
        i18n.language === 'ar' ? 'محامي خاص' : 'Dedicated lawyer',
        i18n.language === 'ar' ? 'تحليل AI متقدم' : 'Advanced AI analysis',
        i18n.language === 'ar' ? 'دعم 24/7' : '24/7 Support',
        i18n.language === 'ar' ? 'أولوية قصوى' : 'Top priority'
      ],
      subscribers: 8,
      badge: i18n.language === 'ar' ? 'VIP' : 'VIP'
    }
  ];

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pkgRes, subRes] = await Promise.all([
        api.get('/packages'),
        api.get('/subscriptions')
      ]);
      setPackages(pkgRes.data.length > 0 ? pkgRes.data : defaultPackages);
      setSubscriptions(subRes.data);
    } catch (error) {
      setPackages(defaultPackages);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPackage = async (e) => {
    e.preventDefault();
    try {
      await api.post('/packages', newPackage);
      toast.success(i18n.language === 'ar' ? 'تم إضافة الباقة بنجاح' : 'Package added successfully');
      setShowAddPackage(false);
      fetchData();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة الباقة' : 'Failed to add package');
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي الباقات' : 'Total Packages',
      value: defaultPackages.length,
      icon: Package,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'المشتركون' : 'Subscribers',
      value: defaultPackages.reduce((sum, p) => sum + p.subscribers, 0),
      icon: Users,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'الإيرادات' : 'Revenue',
      value: `${(defaultPackages.reduce((sum, p) => sum + (p.price * p.subscribers), 0)).toLocaleString()} ${i18n.language === 'ar' ? 'ريال' : 'SAR'}`,
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600'
    }
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'إدارة الباقات' : 'Packages Management'}
              </h1>
              <p className="text-text-secondary text-lg">
                {i18n.language === 'ar' ? 'إدارة باقات الخدمات والاشتراكات' : 'Manage service packages and subscriptions'}
              </p>
            </div>
            <Dialog open={showAddPackage} onOpenChange={setShowAddPackage}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2">
                  <Plus className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'إضافة باقة' : 'Add Package'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{i18n.language === 'ar' ? 'إضافة باقة جديدة' : 'Add New Package'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddPackage} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الاسم بالعربية' : 'Name (Arabic)'}</Label>
                      <Input value={newPackage.name_ar} onChange={(e) => setNewPackage({...newPackage, name_ar: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الاسم بالإنجليزية' : 'Name (English)'}</Label>
                      <Input value={newPackage.name_en} onChange={(e) => setNewPackage({...newPackage, name_en: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'السعر (ريال)' : 'Price (SAR)'}</Label>
                      <Input type="number" value={newPackage.price} onChange={(e) => setNewPackage({...newPackage, price: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'المدة (شهر)' : 'Duration (months)'}</Label>
                      <Input type="number" value={newPackage.duration_months} onChange={(e) => setNewPackage({...newPackage, duration_months: e.target.value})} required />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowAddPackage(false)}>
                      {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="btn-primary">
                      {i18n.language === 'ar' ? 'إضافة' : 'Add'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-2">{stat.title}</p>
                      <p className="text-2xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {defaultPackages.map((pkg, index) => (
              <Card
                key={index}
                className={`relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ${
                  pkg.badge ? 'ring-2 ring-primary' : ''
                }`}
              >
                {pkg.badge && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-primary text-white">{pkg.badge}</Badge>
                  </div>
                )}
                <div className={`absolute inset-0 bg-gradient-to-br ${pkg.color} opacity-5`} />
                <CardHeader className="relative z-10 text-center pb-6">
                  <div className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${pkg.color} mx-auto mb-4 flex items-center justify-center shadow-xl`}>
                    <pkg.icon className="w-10 h-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl mb-2">
                    {i18n.language === 'ar' ? pkg.name_ar : pkg.name_en}
                  </CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar' ? pkg.description_ar : pkg.description_en}
                  </CardDescription>
                  <div className="mt-4">
                    <div className="text-4xl font-bold text-primary">{pkg.price.toLocaleString()}</div>
                    <div className="text-sm text-text-secondary">
                      {i18n.language === 'ar' ? 'ريال' : 'SAR'} / {pkg.duration_months} {i18n.language === 'ar' ? 'شهر' : 'months'}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="relative z-10">
                  <div className="space-y-3 mb-6">
                    {pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${pkg.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button className="w-full btn-primary">
                      {i18n.language === 'ar' ? 'تعديل' : 'Edit'}
                    </Button>
                    <div className="text-center text-sm text-text-secondary">
                      {pkg.subscribers} {i18n.language === 'ar' ? 'مشترك' : 'subscribers'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PackagesManagement;